import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatJMD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackageSearch, CheckCircle2, Clock, Truck, Copy } from "lucide-react";
import { toast } from "sonner";

const CARRIERS = {
  knutsford_islandwide: "Knutsford Express",
  zipmail_postoffice: "ZipMail",
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("ref") || "");
  const [contact, setContact] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    if (!orderNumber.trim() || !contact.trim()) {
      setError("Enter both order reference and phone/email.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/orders/track", { order_number: orderNumber, contact });
      setOrder(r.data);
    } catch {
      setError("We couldn't find an order matching those details. Double check your order reference and try again, or contact us on WhatsApp.");
    }
    setLoading(false);
  };

  const field = "bg-card border-border rounded-none h-11 focus-visible:ring-jamaica-gold";
  const status = order?.order_status || "Pending Payment";
  const shipped = status === "Shipped" || status === "Delivered";
  const carrier = CARRIERS[order?.tracking_carrier] || "the courier";
  const copyTracking = () => { navigator.clipboard.writeText(order.tracking_number || ""); toast.success("Tracking number copied"); };

  return (
    <div data-testid="track-page" className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
      <div className="text-center mb-8">
        <PackageSearch className="w-10 h-10 text-jamaica-gold mx-auto mb-3" />
        <h1 className="font-display text-5xl tracking-tight">TRACK MI ORDER</h1>
        <p className="text-zinc-400 text-sm mt-2">Enter your order reference and the phone or email you used.</p>
      </div>

      <form onSubmit={track} className="space-y-4 border border-white/10 bg-card p-5">
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Order Reference</Label>
          <Input data-testid="track-order-number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} className={field} placeholder="TT-2026-00042" />
        </div>
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Phone or Email used at checkout</Label>
          <Input data-testid="track-contact" value={contact} onChange={(e) => setContact(e.target.value)} className={field} placeholder="876-000-0000 or you@email.com" />
        </div>
        <Button data-testid="track-submit" type="submit" disabled={loading} className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">
          {loading ? "Searching…" : "Track Order"}
        </Button>
        {error && <p data-testid="track-error" className="font-mono text-xs text-red-400 text-center">{error}</p>}
      </form>

      {order && (
        <div data-testid="track-result" className="border border-jamaica-gold/40 bg-card p-5 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-display text-2xl text-jamaica-gold tracking-wider">{order.order_reference || order.order_number}</span>
            <span data-testid="track-status" className={`flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest px-3 py-1 border ${shipped ? "text-jamaica-green border-jamaica-green/50" : "text-jamaica-gold border-jamaica-gold/50"}`}>
              {shipped ? <Truck className="w-3.5 h-3.5" /> : status === "Payment Confirmed" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              {status}
            </span>
          </div>

          {shipped ? (
            <div data-testid="track-shipping" className="mt-4 border border-white/10 p-3">
              <p data-testid="track-carrier-label" className="font-heading font-bold uppercase text-sm">{carrier}</p>
              <div className="flex items-center gap-2 mt-1">
                <span data-testid="tracking-number" className="font-mono text-jamaica-gold text-lg">{order.tracking_number}</span>
                <button onClick={copyTracking} data-testid="copy-tracking" className="text-zinc-400 hover:text-jamaica-gold"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Contact {carrier} directly with this number for live delivery updates.</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-zinc-300">
              {status === "Payment Confirmed" ? "Payment confirmed — your order is being packed." : "We're waiting on your payment. Once it lands, we pack and ship."}
            </p>
          )}

          <div className="mt-4 space-y-1">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-zinc-300">{i.title} <span className="font-mono text-xs text-zinc-500">({i.size} ×{i.qty})</span></span>
                <span className="font-mono text-zinc-400">{formatJMD(i.price_cents * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-3 pt-3 flex justify-between font-mono text-sm">
            <span className="uppercase text-xs text-zinc-400 self-center">Total</span>
            <span className="text-jamaica-gold">{formatJMD(order.total_cents)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
